import {
  increaseLossLimitRequest,
  requestLossLimitRemoval,
  updateLossLimits,
} from "app/api/lossLimitsApi";
import Modal from "app/components/Modal/Modal";
import { LossLimitsType } from "app/types/userType";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LossLimitsActionsSelect from "./LossLimitsActionsSelect";
import UpdateLossLimitsRow from "./UpdateLossLimitsRow";

type props = {
  lossLimits: LossLimitsType | null;
  setLossLimits: React.Dispatch<React.SetStateAction<LossLimitsType | null>>;
  getLossLimits: () => void;
};

export const UpdateLossLimits: React.FC<props> = ({
  lossLimits,
  setLossLimits,
  getLossLimits,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [modalErrorMessage, setModalErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isModalSubmitDisabled, setIsModalSubmitDisabled] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isCheckBoxDisabled, setIsCheckBoxDisabled] = useState<boolean>(true);
  const [checkBoxSelectedStates, setCheckBoxSelectedStates] = useState({
    lossLimitOneDay: false,
    lossLimitSevenDays: false,
    lossLimitThirtyDays: false,
  });

  const intl = useIntl();
  const [editedLimits, setEditedLimits] = useState<LossLimitsType | null>({});

  useEffect(() => {
    // When an action is selected, set the action and reset any selected checkboxes
    setAction(selectedOption ? selectedOption.value : null);
    selectedOption && setIsCheckBoxDisabled(false);
    setCheckBoxSelectedStates({
      lossLimitOneDay: false,
      lossLimitSevenDays: false,
      lossLimitThirtyDays: false,
    });
    setErrorMessage("");
    setModalErrorMessage("");
  }, [selectedOption]);

  useEffect(() => {
    // check that an action and at least one checkbox have been selected
    // if so enable the Submit button
    const isAtLeastOneCheckboxSelected = Object.values(
      checkBoxSelectedStates
    ).some(isSelected => isSelected);
    const isActionSet = action !== "";
    setIsSubmitDisabled(!(isAtLeastOneCheckboxSelected && isActionSet));
  }, [checkBoxSelectedStates, action]);

  useEffect(() => {
    // Show info message based on the selected action
    if (action === "increase") {
      setInfoMessage(
        intl.formatMessage({
          id: "loss-limits-info-message-increase",
          defaultMessage:
            "Once submitted, your request will be updated in 24 - 48 hours, pending confirmation",
        })
      );
      setSuccessMessage("");
    } else if (action === "decrease") {
      setInfoMessage(
        intl.formatMessage({
          id: "loss-limits-info-message-decrease",
          defaultMessage:
            "Once submitted, your new limit will become effective immediately",
        })
      );
    } else {
      setInfoMessage("");
    }
  }, [action, intl]);

  const validateDecreaseLimits = (): boolean => {
    let isValid = true;
    let errorMsg = "";
    Object.entries(checkBoxSelectedStates).forEach(([key, isSelected]) => {
      if (isSelected) {
        const currentLimit = lossLimits?.[key];
        const newLimit = editedLimits?.[key];
        if (
          newLimit === "" ||
          newLimit === null ||
          newLimit === undefined ||
          newLimit >= currentLimit
        ) {
          isValid = false;
          errorMsg = intl.formatMessage({
            id: "loss-limits-decrease-error",
            defaultMessage:
              "The new limit should be set lower than the existing limit.",
          });
        }
      }
    });

    if (!isValid) {
      setErrorMessage(errorMsg);
    } else {
      setErrorMessage("");
    }

    return isValid;
  };

  const validateIncreaseLimits = (): boolean => {
    let isValid = true;
    let errorMsg = "";

    Object.entries(checkBoxSelectedStates).forEach(([key, isSelected]) => {
      if (isSelected) {
        const currentLimit = lossLimits?.[key];
        const newLimit = editedLimits?.[key];
        if (
          newLimit === "" ||
          newLimit === null ||
          newLimit === undefined ||
          newLimit <= currentLimit
        ) {
          isValid = false;
          errorMsg = intl.formatMessage({
            id: "loss-limits-increase-error",
            defaultMessage:
              "The new limit must be higher than the previous limit.",
          });
        }
      }
    });

    if (!isValid) {
      setErrorMessage(errorMsg);
    } else {
      setErrorMessage("");
    }

    return isValid;
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setIsSubmitDisabled(true);
    setIsCheckBoxDisabled(true);
  };

  const decreaseLossLimit = () => {
    if (!validateDecreaseLimits()) {
      return;
    }
    updateLossLimits(editedLimits)
      .then((resp: LossLimitsType) => {
        setLossLimits(resp);
        setErrorMessage("");
        setInfoMessage("");
        setSuccessMessage(
          intl.formatMessage({
            id: "loss-limits-updated",
            defaultMessage: "Limit Updated",
          })
        );
        // Remove success message after 5 seconds
        const timeoutId = setTimeout(() => {
          setSuccessMessage("");
        }, 5000);

        // Cleanup function to clear the timeout if the component unmounts
        return () => clearTimeout(timeoutId);
      })
      .catch(error => {
        console.log("error: ", error);
        setErrorMessage(formatErrors(error));
      });
  };

  const formatErrors = (error: any) => {
    const errorMessage = get(
      error,
      "response.data.errors[0].messages[0]",
      get(
        error,
        "response.data.message",
        get(
          error,
          "response.data.errors[0].message",
          get(error, "message", "An unknown error occurred.")
        )
      )
    );
    return errorMessage;
  };

  const handleCheckboxChange = (id: string, value: boolean) => {
    // If the checkbox is selected, set the edited limit to the current limit
    // If the checkbox is not selected, remove the edited limit
    setCheckBoxSelectedStates(prevState => ({
      ...prevState,
      [id]: value,
    }));
    if (value && lossLimits && lossLimits[id] !== undefined) {
      // checkbox is selected
      setEditedLimits(prevLimits => ({
        ...prevLimits,
        [id]: lossLimits[id],
      }));
    } else {
      // checkbox is not selected
      setEditedLimits(prevLimits => {
        const updatedLimits = { ...prevLimits };
        delete updatedLimits[id];
        return updatedLimits;
      });
      setErrorMessage("");
    }
  };

  const increaseLossLimit = () => {
    if (!validateIncreaseLimits()) {
      return;
    }

    increaseLossLimitRequest(editedLimits)
      .then((resp: string) => {
        // eg resp: "Your request has been received and is under review.A member of our support team will be in contact in 24-48 hours."
        setSuccessMessage(resp);
        getLossLimits();
      })
      .catch(error => {
        console.log("error: ", error);
        setErrorMessage(formatErrors(error));
        setInfoMessage("");
        setIsSubmitDisabled(true);
      })
      .finally(() => {
        setInfoMessage("");
      });
  };

  const removeLossLimit = () => {
    // Find which checkboxes are selected
    const selectedCheckboxNames = Object.entries(checkBoxSelectedStates)
      .filter(([checkboxName, isChecked]) => isChecked === true)
      .map(([checkboxName, _]) => checkboxName);

    // Send value of zero for each selected limit, to request removal of the limit
    const newLossLimits: LossLimitsType = {};
    if (lossLimits) {
      selectedCheckboxNames.forEach(checkboxName => {
        if (
          checkboxName in lossLimits &&
          lossLimits[checkboxName] !== undefined
        ) {
          newLossLimits[checkboxName] = 0;
        }
      });
    }

    requestLossLimitRemoval(newLossLimits)
      .then(resp => {
        //EG of resp: "Your request has been received and is under review.A member of our support team will be in contact in 24-48 hours."
        setSuccessMessage(resp);
        setIsModalVisible(false);
        getLossLimits();
      })
      .catch(error => {
        console.log("error: ", error);
        const errorMessage = formatErrors(error);
        setModalErrorMessage(errorMessage);
        setErrorMessage(errorMessage);
        setIsModalSubmitDisabled(true);
        getLossLimits();
        //   {
        //     "errors": [
        //         {
        //             "field": "Loss limits",
        //             "messages": [
        //                 "You have to wait for 48 hours between two removal requests."
        //             ]
        //         }
        //     ]
        // }
      })
      .finally(() => {
        getLossLimits();
      });
  };

  const submit = () => {
    switch (action) {
      case "remove":
        setIsModalVisible(true);
        break;
      case "increase":
        increaseLossLimit();
        break;
      case "decrease":
        decreaseLossLimit();
        break;
      default:
        break;
    }
  };
  const updateLossLimit = (
    id: keyof LossLimitsType,
    newLimitValue: number | string | null
  ) => {
    // Set the new limit value in the editedLimits object
    setEditedLimits(prevLimits => ({
      ...prevLimits,
      [id]: newLimitValue !== null ? newLimitValue : null,
    }));
    setIsSubmitDisabled(false);
  };

  return (
    <div>
      <div className="update-loss-limits__header">
        <h4>
          <FormattedMessage
            id="loss-limits-edit"
            defaultMessage="Edit Loss Limits"
          />
        </h4>
        <LossLimitsActionsSelect
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
      </div>
      <div className="update-loss-limits__rows">
        {lossLimits && lossLimits.lossLimitOneDay !== null && (
          <UpdateLossLimitsRow
            key="lossLimitOneDay"
            id={"lossLimitOneDay"}
            lossLimit={lossLimits.lossLimitOneDay}
            newLimit={editedLimits?.lossLimitOneDay}
            label={intl.formatMessage({
              id: "loss-limits-daily",
              defaultMessage: "Daily",
            })}
            updateLossLimit={updateLossLimit}
            isCheckBoxDisabled={isCheckBoxDisabled}
            isCheckBoxSelected={checkBoxSelectedStates.lossLimitOneDay}
            handleCheckboxChange={async value =>
              handleCheckboxChange("lossLimitOneDay", value)
            }
          />
        )}
        {lossLimits && lossLimits.lossLimitSevenDays !== null && (
          <UpdateLossLimitsRow
            key="lossLimitSevenDays"
            id={"lossLimitSevenDays"}
            lossLimit={lossLimits.lossLimitSevenDays}
            newLimit={editedLimits?.lossLimitSevenDays}
            label={intl.formatMessage({
              id: "loss-limits-weekly",
              defaultMessage: "Weekly",
            })}
            updateLossLimit={updateLossLimit}
            isCheckBoxDisabled={isCheckBoxDisabled}
            isCheckBoxSelected={checkBoxSelectedStates.lossLimitSevenDays}
            handleCheckboxChange={async value =>
              handleCheckboxChange("lossLimitSevenDays", value)
            }
          />
        )}
        {lossLimits && lossLimits.lossLimitThirtyDays !== null && (
          <UpdateLossLimitsRow
            key="lossLimitThirtyDays"
            id={"lossLimitThirtyDays"}
            lossLimit={lossLimits.lossLimitThirtyDays}
            newLimit={editedLimits?.lossLimitThirtyDays}
            label={intl.formatMessage({
              id: "loss-limits-monthly",
              defaultMessage: "Monthly",
            })}
            updateLossLimit={updateLossLimit}
            isCheckBoxDisabled={isCheckBoxDisabled}
            isCheckBoxSelected={checkBoxSelectedStates.lossLimitThirtyDays}
            handleCheckboxChange={async value =>
              handleCheckboxChange("lossLimitThirtyDays", value)
            }
          />
        )}
      </div>
      <div className="loss-limits__buttons">
        <button
          type="button"
          onClick={submit}
          className="loss-limits__buttons__submit-button"
          disabled={isSubmitDisabled}
        >
          <FormattedMessage id="button-submit" defaultMessage="Submit" />
        </button>
      </div>
      <div className="update-loss-limits__messages">
        {infoMessage && <span>{infoMessage}</span>}
        {successMessage && (
          <span className="loss-limits__messages__success-message">
            {successMessage}
          </span>
        )}
        {errorMessage && (
          <div className="loss-limits__messages__error-message">
            {errorMessage}
          </div>
        )}
      </div>
      <Modal
        modalOpen={isModalVisible}
        closeModal={closeModal}
        contentLabel="Loss Limits"
        cancelButton
        cancelButtonText={intl.formatMessage({
          id: "button-cancel",
          defaultMessage: "Cancel",
        })}
        className="loss-limits-modal"
      >
        <div className="loss-limit-remove-container">
          <h2>
            <FormattedMessage
              id="loss-limits-confirm-remove"
              defaultMessage={"Confirm Limit Removal"}
            />
          </h2>
          <span className="loss-limits-text">
            <FormattedMessage
              id="loss-limits-confirm-remove-message"
              defaultMessage={
                "Your request to remove the limit will be submitted. You'll receive an update within 24-48 hours, pending confirmation."
              }
            />
          </span>
          <div className="loss-limit-remove">
            <button
              type="button"
              onClick={removeLossLimit}
              className="loss-limits__submit-button"
              disabled={isModalSubmitDisabled}
            >
              <FormattedMessage
                id="loss-limits-remove"
                defaultMessage="Remove Limit"
              />
            </button>
          </div>
          {modalErrorMessage && (
            <div className="loss-limit-remove-container__error-message">
              {modalErrorMessage}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UpdateLossLimits;
